/**
 * Shared moto helper for Throttle (slot 15) and Slipstream (slot 18).
 *
 * Per Throttle §02 §2.1 design dossier: this module exists so the
 * bike geometry and engine audio surface can be amortized across both
 * games. Same layering pattern as the existing `_lib/hex.ts` shared
 * between Lattice and Resonance.
 *
 * Rule: this module never imports from `games/throttle/` or
 * `games/slipstream/`. Per-game modules import from here, not the
 * other way around.
 */

import * as THREE from "three";

/** Authoritative physics constants. Both games + the server validator
 *  read these. Drift would silently desync the visual from the
 *  scoring; see Throttle §02 §4.2 parity contract row. */
export const BIKE_MASS = 1.0;
export const WHEEL_RADIUS = 0.4;
export const CHASSIS_HALF_LENGTH = 0.8;
export const CHASSIS_HEIGHT = 0.5;

export interface BikePalette {
  chassis: number; // hex color
  emissive: number;
  wheels: number;
  rider: number;
}

export const PALETTE_HELIOS: BikePalette = {
  chassis: 0xff5b3a,
  emissive: 0xff2010,
  wheels: 0x222234,
  rider: 0xffe9b8,
};

export const PALETTE_CYAN: BikePalette = {
  chassis: 0x46ffe0,
  emissive: 0x00aa90,
  wheels: 0x111122,
  rider: 0x66ddff,
};

/**
 * Build a side-view procedural bike mesh: chassis box + two wheel
 * cylinders + rider sphere. Returned as a `THREE.Group` so callers
 * can rotate it as a single unit (lean / flip).
 *
 * Slipstream's first-person view will mount the same mesh and orient
 * its camera at the rider position with chassis pointed forward.
 */
export function buildBikeMesh(palette: BikePalette = PALETTE_HELIOS): THREE.Group {
  const group = new THREE.Group();

  // Chassis: a tilted box centered at origin.
  const chassisGeo = new THREE.BoxGeometry(
    CHASSIS_HALF_LENGTH * 2,
    CHASSIS_HEIGHT,
    0.3,
  );
  const chassisMat = new THREE.MeshStandardMaterial({
    color: palette.chassis,
    emissive: palette.emissive,
    emissiveIntensity: 0.8,
    roughness: 0.4,
    metalness: 0.6,
  });
  const chassis = new THREE.Mesh(chassisGeo, chassisMat);
  chassis.position.y = WHEEL_RADIUS + CHASSIS_HEIGHT * 0.5;
  group.add(chassis);

  // Wheels: two cylinders mounted at the front and rear of the chassis.
  const wheelGeo = new THREE.CylinderGeometry(WHEEL_RADIUS, WHEEL_RADIUS, 0.18, 18);
  wheelGeo.rotateX(Math.PI / 2); // align wheel disc to the side-view plane
  const wheelMat = new THREE.MeshStandardMaterial({
    color: palette.wheels,
    roughness: 0.7,
    metalness: 0.2,
  });
  const wheelFront = new THREE.Mesh(wheelGeo, wheelMat);
  wheelFront.position.set(CHASSIS_HALF_LENGTH * 0.8, WHEEL_RADIUS, 0);
  group.add(wheelFront);
  const wheelRear = new THREE.Mesh(wheelGeo, wheelMat);
  wheelRear.position.set(-CHASSIS_HALF_LENGTH * 0.8, WHEEL_RADIUS, 0);
  group.add(wheelRear);

  // Rider: sphere above the chassis, slightly forward.
  const riderGeo = new THREE.SphereGeometry(0.22, 14, 10);
  const riderMat = new THREE.MeshStandardMaterial({
    color: palette.rider,
    emissive: palette.rider,
    emissiveIntensity: 0.3,
    roughness: 0.5,
    metalness: 0.3,
  });
  const rider = new THREE.Mesh(riderGeo, riderMat);
  rider.position.set(0.15, WHEEL_RADIUS + CHASSIS_HEIGHT + 0.15, 0);
  group.add(rider);

  return group;
}

/**
 * Smooth discrete keyboard lean input into a continuous axis.
 * Used by both Throttle's per-game and (future) Slipstream's per-game
 * for the same reason: keyboard lean is a digital -1 / 0 / +1 signal
 * but the bike physics wants a continuous axis. We approach the
 * target at maxRate per second and release to zero at the same rate.
 *
 * Per Throttle §02 §2.1 component table.
 */
export function clampLean(
  current: number,
  target: number,
  dt: number,
  maxRate: number = 2.0,
): number {
  const delta = target - current;
  const step = maxRate * dt;
  if (Math.abs(delta) <= step) return target;
  return current + Math.sign(delta) * step;
}
